/**
 * RegistrationIntakeService
 * Service for actioning public course registration intakes (approve / reject)
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for the admin intake queue: paged list views, detail views and the
 * approve/reject decisions
 */
export class RegistrationIntakeService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? RegistrationIntakeService.DEFAULT_SERVICE_PATH,
            basePath: RegistrationIntakeService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Get the paged intake list view (filtered)
     * @param filter - Optional filter parameters
     * @returns Paged intake list view response
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        if (!response.data) {
            throw new Error('Failed to retrieve registration intake list view');
        }
        return response.data;
    }
    /**
     * Get the paged intake list view (filtered) with full response
     * @param filter - Optional filter parameters
     * @returns ResponseData with paged intake list view response
     */
    listViewRaw(filter) {
        const url = this.buildUrl('list-view');
        return this.http.post(url, filter ?? {});
    }
    /**
     * Get an intake detail view by ID
     * @param id - The intake ID
     * @returns Registration intake model view
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Registration intake with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get an intake detail view by ID with full response
     * @param id - The intake ID
     * @returns ResponseData with registration intake model view
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    /**
     * Approve an intake (creates the registration)
     * @param id - The intake ID
     * @param request - Optional approval payload (date override, facilitators, note)
     * @returns The created registration
     */
    async approve(id, request) {
        const response = await this.approveRaw(id, request);
        if (!response.data) {
            throw new Error(`Failed to approve registration intake ${id}`);
        }
        return response.data;
    }
    /**
     * Approve an intake with full response
     * @param id - The intake ID
     * @param request - Optional approval payload
     * @returns ResponseData with the created registration
     */
    approveRaw(id, request) {
        const url = this.buildUrl(`${id}/approve`);
        return this.http.post(url, request ?? {});
    }
    /**
     * Reject an intake
     * @param id - The intake ID
     * @param request - Rejection payload with reason
     * @returns True if the rejection succeeded
     */
    async reject(id, request) {
        const response = await this.rejectRaw(id, request);
        return response.success;
    }
    /**
     * Reject an intake with full response
     * @param id - The intake ID
     * @param request - Rejection payload with reason
     * @returns ResponseData (success/fail)
     */
    rejectRaw(id, request) {
        const url = this.buildUrl(`${id}/reject`);
        return this.http.post(url, request);
    }
    /**
     * Soft-delete an intake off the queue (spam/junk). Refuses (400) an approved intake — it has a
     * registration behind it, and deleting would orphan `Registration.intakeID`. Cancelling a genuine
     * withdrawal is a different action (`changeStatus(id, -2)`) and is left untouched. ActsCareAdmin only.
     * DELETE api/registration-intakes/{id}
     * @param id - The intake ID
     * @returns True if the intake was removed (false when it was approved or not found)
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    deleteRaw(id) {
        const url = this.buildUrl(`${id}`);
        return this.http.delete(url);
    }
    /**
     * Soft-delete many intakes off the queue in one call. Approved/missing intakes are skipped server-side,
     * so this reports partial success honestly. ActsCareAdmin only.
     * POST api/registration-intakes/delete-many
     * @param ids - The intake IDs to delete
     * @returns How many intakes were actually removed
     */
    async deleteMany(ids) {
        const response = await this.deleteManyRaw(ids);
        return response.data?.removed ?? 0;
    }
    deleteManyRaw(ids) {
        const url = this.buildUrl('delete-many');
        return this.http.post(url, { ids });
    }
    /**
     * Move an intake between statuses (e.g. Pending ↔ Cancelled).
     * ⚠️ 400s on Approved (1) and Rejected (-1) — those are workflow-owned and must go through
     * approve() / reject(), which create the registration / run the decline flow. Mirrors
     * CareRequestService.changeStatus.
     * @param id - The intake ID
     * @param newStatus - The new IntakeStatusID (0 Pending, -2 Cancelled; 1 / -1 are rejected here)
     * @returns The updated intake
     */
    async changeStatus(id, newStatus) {
        const response = await this.changeStatusRaw(id, newStatus);
        if (!response.data) {
            throw new Error(`Failed to change status for intake ${id}`);
        }
        return response.data;
    }
    changeStatusRaw(id, newStatus) {
        const url = this.buildUrl(`${id}/change-status/${newStatus}`);
        return this.http.patch(url);
    }
    /**
     * Ranked candidate member matches for one participant (by exact email, phone, or name), so the admin
     * can pick the right person — or 'create new' — before approving. Send the chosen memberIDs back on
     * the approve payload (`participantMembers`). An empty list = no candidates → create new.
     * POST api/registration-intakes/candidate-matches
     */
    async candidateMatches(query) {
        const response = await this.candidateMatchesRaw(query);
        return response.data ?? [];
    }
    candidateMatchesRaw(query) {
        const url = this.buildUrl('candidate-matches');
        return this.http.post(url, query);
    }
}
RegistrationIntakeService.DEFAULT_SERVICE_PATH = 'ac-courses';
RegistrationIntakeService.BASE_PATH = 'api/registration-intakes';
//# sourceMappingURL=RegistrationIntakeService.js.map